import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { createForm } from "@formily/core";
import SchemaFormView from "../../../components/Schema/SchemaFormView";
import { SchemaService, DynamicModuleService } from "../../../services/APIs";
import { AppLayout } from "@project/components/AppLayout";
import { removeMetaFields } from "../../../lib/removeMetaFields";
import { notify } from "../../../components/common/Notification";
import { Modal, Spin, Popconfirm } from "antd";
import { Input } from "@project/components/ui/input";
import { Button } from "@project/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Textarea } from "@project/components/ui/textarea";
import { Label } from "@project/components/ui/label";
import {
  Search,
  Calendar,
  Clock,
  Plus,
  Sparkles,
  Loader2,
  MessageSquare,
  Star,
  Brain,
  Trash2,
  Pencil,
} from "lucide-react";
import { motion } from "framer-motion";
import {
  staggerContainer as container,
  fadeInUp as item,
} from "@project/lib/animations";
import { toast } from "@project/hooks/use-toast";
import { useAIAction } from "@project/hooks/useAIAction";
import AIResultDisplay from "@project/components/AIResultDisplay";

const formatDate = (val: string | undefined) => {
  if (!val) return "-";
  const d = new Date(val);
  if (!isNaN(d.getTime())) {
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  }
  return val;
};

const formatTime12h = (val: string | undefined) => {
  if (!val) return "-";
  // Handle "HH:MM:SS" or "HH:MM" format
  const match = val.match(/^(\d{1,2}):(\d{2})/);
  if (match) {
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${ampm}`;
  }
  // Handle ISO datetime string
  const d = new Date(val);
  if (!isNaN(d.getTime())) {
    return d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
  }
  return val;
};

const statusColors: Record<string, string> = {
  Scheduled:
    "text-[hsl(var(--status-posted))] bg-[hsl(var(--status-posted-bg))]",
  Completed:
    "text-[hsl(var(--status-active))] bg-[hsl(var(--status-active-bg))]",
  Cancelled: "text-destructive bg-[hsl(var(--status-filled-bg))]",
  "No Show":
    "text-[hsl(var(--status-closed))] bg-[hsl(var(--status-closed-bg))]",
};

const InterviewsPage = () => {
  const [activeTab, setActiveTab] = useState("All");
  const [search, setSearch] = useState("");
  const [selectedInterview, setSelectedInterview] = useState<any | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  const {
    data: interviewsData,
    isLoading: interviewsLoading,
    refetch: refetchInterviews,
  } = useQuery({
    queryKey: ["interviews-data", page, PAGE_SIZE],
    queryFn: () =>
      DynamicModuleService.getGridCoByConfig(
        "Schedule Interview",
        page,
        PAGE_SIZE,
        [],
        {}
      ),
  });

  const interviews: any[] = interviewsData?.results ?? [];
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [aiQuestionsOpen, setAiQuestionsOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiQuestions, setAiQuestions] = useState<string[]>([]);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editInterviewId, setEditInterviewId] = useState<string | null>(null);
  const [editInterviewData, setEditInterviewData] = useState<any>(null);
  const [fbRating, setFbRating] = useState(0);
  const [fbStrengths, setFbStrengths] = useState("");
  const [fbWeaknesses, setFbWeaknesses] = useState("");
  const [fbNotes, setFbNotes] = useState("");
  const [fbRecommendation, setFbRecommendation] = useState<
    "Hire" | "Reject" | "Next Round" | "Hold"
  >("Next Round");
  const scheduleForm = useMemo(() => {
    const f = createForm({});
    if (editMode && editInterviewData) {
      f.setInitialValues(editInterviewData);
    }
    return f;
  }, [scheduleOpen]);

  const { data: scheduleSchemaInfo, isLoading: scheduleSchemaLoading } = useQuery({
    queryKey: ["schema", "Schedule Interview"],
    queryFn: () => SchemaService.getSchemaByName("Schedule Interview"),
  });

  // Build candidate ID → name map from schema enum
  const candidateMap: Record<string, string> = useMemo(() => {
    const schema = scheduleSchemaInfo?.json ?? scheduleSchemaInfo;
    const enumOptions: { label: string; value: string | number }[] =
      schema?.schema?.properties?.candidate?.enum ?? [];
    const map: Record<string, string> = {};
    enumOptions.forEach((opt: any) => {
      map[String(opt.value)] = opt.label;
    });
    return map;
  }, [scheduleSchemaInfo]);

  const [aiSummaryOpen, setAiSummaryOpen] = useState(false);
  const aiSummary = useAIAction();

  const tabFilters = [
    { label: "All", count: interviews.length },
    { label: "Scheduled", count: interviews.filter(i => i.status === "Scheduled").length },
    { label: "Completed", count: interviews.filter(i => i.status === "Completed").length },
  ];

  const filtered = (
    activeTab === "All"
      ? interviews
      : interviews.filter(i => i.status === activeTab)
  ).filter(i => {
    if (!search) return true;
    const lower = search.toLowerCase();
    return (
      i.candidate?.toLowerCase().includes(lower) ||
      i.job_title?.toLowerCase().includes(lower) ||
      i.interviewer?.toLowerCase().includes(lower) ||
      i.round?.toLowerCase().includes(lower)
    );
  });

  const handleGenerateQuestions = async (interview: any) => {
    toast({
      title: "Feature Disabled",
      description:
        "AI Interview Question generation is currently unavailable because Supabase has been removed.",
      variant: "destructive",
    });
  };

  const handleSubmitFeedback = async () => {
    if (!selectedInterview) return;
    try {
      await DynamicModuleService.updateItem("Schedule Interview", selectedInterview.id, {
        status: "Completed",
        feedback_rating: fbRating,
        feedback_strengths: fbStrengths,
        feedback_weaknesses: fbWeaknesses,
        feedback_notes: fbNotes,
        feedback_recommendation: fbRecommendation,
      });
      setFeedbackOpen(false);
      refetchInterviews();
      toast({ title: "Feedback Submitted" });
    } catch (error) {
      console.error("Error submitting feedback:", error);
      notify.error("Error", "Could not submit feedback.");
    }
  };

  const handleEditInterview = async (interview: any) => {
    try {
      const data = await DynamicModuleService.getItemById("Schedule Interview", interview.id);
      setEditMode(true);
      setEditInterviewId(interview.id);
      setEditInterviewData(data);
      setScheduleOpen(true);
    } catch (error) {
      console.error("Error fetching interview:", error);
      notify.error("Error", "Could not load interview data.");
    }
  };

  const handleDeleteInterview = async (interview: any) => {
    try {
      await DynamicModuleService.deleteItem("Schedule Interview", interview.id);
      refetchInterviews();
    } catch (error) {
      console.error("Error deleting interview:", error);
      notify.error("Delete Failed", "Could not delete the interview.");
    }
  };

  const handleSchedule = async () => {
    try {
      await scheduleForm.validate();
    } catch {
      return;
    }
    try {
      const values = scheduleForm.values;
      if (editMode && editInterviewId) {
        await DynamicModuleService.updateItem("Schedule Interview", editInterviewId, values);
        toast({ title: "Interview Updated" });
      } else {
        await DynamicModuleService.createItem("Schedule Interview", values);
        toast({ title: "Interview Scheduled" });
      }
      setScheduleOpen(false);
      setEditMode(false);
      setEditInterviewId(null);
      setEditInterviewData(null);
      refetchInterviews();
    } catch (error) {
      console.error("Error saving interview:", error);
      toast({ title: "Failed to save interview", variant: "destructive" });
    }
  };

  return (
    <AppLayout>
      <motion.div
        className="space-y-4"
        variants={container}
        initial="hidden"
        animate="show"
      >
        <motion.div
          variants={item}
          className="flex items-center justify-between"
        >
          <div>
            <h1 className="font-display text-lg font-bold tracking-tight text-foreground">
              Interviews
            </h1>
            <p className="mt-0.5 hidden text-[12px] text-muted-foreground sm:block">
              Schedule, track, and manage interview rounds
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="ai-btn h-8 gap-1.5 rounded-lg text-[12px] font-semibold"
              onClick={async () => {
                setAiSummaryOpen(true);
                await aiSummary.run(`Analyze these scheduled interviews and provide insights:
${interviews.map(i => `- Candidate: ${i.candidate}, Round: ${i.round}, Interviewer: ${i.interviewer}, Date: ${i.date}, Status: ${i.status}`).join("\n")}

Provide:
1. **Overall Summary** - aggregate view of all interviews
2. **Upcoming Interviews** - what's scheduled
3. **Completed Interviews** - outcomes
4. **Bottlenecks** - any delays or issues
5. **Next Steps** - suggested actions`);
              }}
            >
              <Brain className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">AI Summary</span>
            </Button>
            <Button
              size="sm"
              onClick={() => setScheduleOpen(true)}
              className="btn-glow h-8 gap-1.5 rounded-lg text-[12px] font-semibold"
            >
              <Plus className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Schedule Interview</span>
              <span className="sm:hidden">Schedule</span>
            </Button>
          </div>
        </motion.div>

        <motion.div
          variants={item}
          className="no-scrollbar flex items-center gap-0.5 overflow-x-auto rounded-xl bg-muted/50 p-0.5"
        >
          {tabFilters.map(tab => (
            <button
              key={tab.label}
              onClick={() => setActiveTab(tab.label)}
              className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-[12px] font-medium transition-all ${activeTab === tab.label ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >
              {tab.label}
              <span
                className={`rounded-md px-1.5 py-0 text-[10px] font-bold ${activeTab === tab.label ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </motion.div>

        <motion.div variants={item}>
          <div className="relative max-w-xs">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-8 rounded-lg border-border/60 bg-card pl-8 text-[12px]"
            />
          </div>
        </motion.div>

        <motion.div variants={item} className="space-y-2">
          {interviewsLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Spin size="large" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="section-card p-8 text-center">
              <Calendar className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
              <p className="text-[13px] text-muted-foreground">
                No interviews found
              </p>
            </div>
          ) : (
            filtered.map(interview => {
              const candidateLabel =
                candidateMap[String(interview.candidate)] ??
                interview.candidate ??
                "-";
              const avatarText = candidateLabel
                .split(" ")
                .slice(0, 2)
                .map((w: string) => w[0])
                .join("")
                .toUpperCase() || "??";
              return (
                <div key={interview.id} className="section-card p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-glow">
                      <span className="text-[11px] font-bold text-white">
                        {avatarText}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-[13px] font-semibold text-foreground">
                          {candidateLabel}
                        </p>
                        {interview.status && (
                          <span
                            className={`rounded-lg px-2 py-0.5 text-[10px] font-semibold ${
                              statusColors[interview.status] ?? "text-muted-foreground bg-muted"
                            }`}
                          >
                            {interview.status}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        {interview.job_title ?? interview.job ?? "-"}{interview.round ? ` · ${interview.round}` : ""}
                      </p>
                    </div>
                    <div className="hidden shrink-0 text-right sm:block">
                      <p className="flex items-center gap-1 text-[12px] font-semibold text-foreground">
                        <Calendar className="h-3 w-3" />
                        {formatDate(interview.date)}
                      </p>
                      <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {formatTime12h(interview.time)}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        onClick={() => {
                          setSelectedInterview(interview);
                          handleGenerateQuestions(interview);
                        }}
                        className="ai-btn-outline rounded-lg p-2 transition-colors"
                        title="AI Questions"
                      >
                        <Sparkles className="h-4 w-4" />
                      </button>
                      {interview.status === "Scheduled" && (
                        <button
                          onClick={() => {
                            setSelectedInterview(interview);
                            setFeedbackOpen(true);
                          }}
                          className="rounded-lg p-2 transition-colors hover:bg-muted"
                          title="Feedback"
                        >
                          <MessageSquare className="h-4 w-4 text-muted-foreground" />
                        </button>
                      )}
                      <button
                        onClick={() => handleEditInterview(interview)}
                        className="rounded-lg p-2 transition-colors hover:bg-muted"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4 text-muted-foreground" />
                      </button>
                      <Popconfirm
                        title="Delete this interview?"
                        onConfirm={() => handleDeleteInterview(interview)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <button
                          className="rounded-lg p-2 transition-colors hover:bg-muted"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </button>
                      </Popconfirm>
                    </div>
                  </div>
                  {(interview.feedback_rating || interview.interviewer) && (
                    <div className="mt-3 flex items-center gap-4 border-t border-border/30 pt-3 text-[11px]">
                      {interview.feedback_rating && (
                        <div className="flex items-center gap-1">
                          <Star className="h-3 w-3 fill-warning text-warning" />
                          <span className="font-bold text-foreground">
                            {interview.feedback_rating}/5
                          </span>
                        </div>
                      )}
                      {interview.feedback_recommendation && (
                        <span
                          className={`font-semibold ${
                            interview.feedback_recommendation === "Hire"
                              ? "text-[hsl(var(--status-active))]"
                              : interview.feedback_recommendation === "Reject"
                              ? "text-destructive"
                              : "text-[hsl(var(--status-onhold))]"
                          }`}
                        >
                          {interview.feedback_recommendation}
                        </span>
                      )}
                      {interview.interviewer && (
                        <span className="text-muted-foreground">
                          by {interview.interviewer}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </motion.div>
      </motion.div>

      {/* Schedule / Edit Interview Modal (Ant Design — avoids Radix pointer-events conflict with Ant Design Select) */}
      <Modal
        open={scheduleOpen}
        onCancel={() => {
          setScheduleOpen(false);
          setEditMode(false);
          setEditInterviewId(null);
          setEditInterviewData(null);
        }}
        title={editMode ? "Edit Interview" : "Schedule Interview"}
        width={600}
        footer={[
          <Button
            key="cancel"
            variant="outline"
            onClick={() => {
              setScheduleOpen(false);
              setEditMode(false);
              setEditInterviewId(null);
              setEditInterviewData(null);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="schedule"
            onClick={handleSchedule}
            className="btn-glow ml-2"
          >
            {editMode ? "Update Interview" : "Schedule"}
          </Button>,
        ]}
        destroyOnClose
        styles={{
          body: {
            maxHeight: "70vh",
            overflowY: "auto",
            paddingTop: 8,
            scrollbarWidth: "none",
          },
        }}
      >
        {scheduleSchemaLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : scheduleSchemaInfo ? (
          <SchemaFormView
            form={scheduleForm}
            schemaJson={scheduleSchemaInfo?.json ?? scheduleSchemaInfo}
          />
        ) : (
          <div className="flex h-40 items-center justify-center text-[13px] text-muted-foreground">
            Schema "Schedule Interview" not found. Please configure it in admin.
          </div>
        )}
      </Modal>

      <Dialog open={aiQuestionsOpen} onOpenChange={setAiQuestionsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Sparkles className="h-4 w-4 text-primary" />
              AI Interview Questions
            </DialogTitle>
          </DialogHeader>
          {aiLoading ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-[13px] text-muted-foreground">Generating...</p>
            </div>
          ) : (
            <div className="space-y-3 pt-2">
              {aiQuestions.map((q, i) => (
                <div key={i} className="flex gap-3 rounded-xl bg-muted/30 p-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-[11px] font-bold text-primary">
                    {i + 1}
                  </span>
                  <p className="text-[12px] leading-relaxed text-foreground">
                    {q}
                  </p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={feedbackOpen} onOpenChange={setFeedbackOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">
              Interview Feedback
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-[12px]">Rating</Label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(n => (
                  <button
                    key={n}
                    onClick={() => setFbRating(n)}
                    className={`h-8 w-8 rounded-lg text-[13px] font-bold transition-all ${fbRating >= n ? "bg-warning text-white" : "bg-muted text-muted-foreground"}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[12px]">Strengths</Label>
              <Input
                value={fbStrengths}
                onChange={e => setFbStrengths(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[12px]">Weaknesses</Label>
              <Input
                value={fbWeaknesses}
                onChange={e => setFbWeaknesses(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[12px]">Notes</Label>
              <Textarea
                value={fbNotes}
                onChange={e => setFbNotes(e.target.value)}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[12px]">Recommendation</Label>
              <Select
                value={fbRecommendation}
                onValueChange={v => setFbRecommendation(v as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Hire">✅ Hire</SelectItem>
                  <SelectItem value="Next Round">🔄 Next Round</SelectItem>
                  <SelectItem value="Hold">⏸ Hold</SelectItem>
                  <SelectItem value="Reject">❌ Reject</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSubmitFeedback} className="btn-glow w-full">
              Submit Feedback
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Interview Summary Dialog */}
      <Dialog open={aiSummaryOpen} onOpenChange={setAiSummaryOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Brain className="h-5 w-5 text-primary" />
              AI Interview Summary Report
            </DialogTitle>
          </DialogHeader>
          {aiSummary.loading ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-[13px] text-muted-foreground">
                Analyzing all interview feedback...
              </p>
            </div>
          ) : (
            <AIResultDisplay content={aiSummary.result} />
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default InterviewsPage;
